import { api } from "./api";
import { ListRequest, MessageResponse } from "../types/common/commonTypes";
import {
  InvStatusFull,
  InvStatusCreate,
  InvStatusUpdate,
  PaginatedInvStatusResponse,
} from "../types/invStatusTypes";

/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const status_base = "/inventory/status";
export const lotStatusService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================

  list: async (params: ListRequest): Promise<PaginatedInvStatusResponse> => {
    const response = await api.post(`${status_base}/search`, params);
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (id: string): Promise<InvStatusFull> => {
    const response = await api.get(`${status_base}/${id}`);
    return response.data;
  },

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: InvStatusCreate): Promise<InvStatusFull> => {
    const response = await api.post(`${status_base}/`, data);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (id: string, data: InvStatusUpdate): Promise<InvStatusFull> => {
    const response = await api.put(`${status_base}/${id}`, { ...data, id });
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (id: string): Promise<MessageResponse> => {
    const response = await api.delete(`${status_base}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (id: string): Promise<InvStatusFull> => {
    const response = await api.post(`${status_base}/${id}/restore`);
    return response.data;
  },
};
